import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { OFFLINE_STORES, getOfflineStorageDriver, getEffectiveStorageEngine } from './offlineStorageAdapter.js';
import { readStoredAuth } from '../api/authApi.js';
import { requestWithFallback } from '../api/httpClient.js';
import { jornadaSyncError } from './jornadaOfflineService.js';
import { ASISTENCIA_DOCUMENTOS_MAX, validarDocumentoAsistencia } from './asistenciaDocumentos.js';

const store = OFFLINE_STORES.RRHH;
export const ASISTENCIA_CHANGED = 'asistencia:changed';
const notify = (userId, remoteChanged = false) => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ASISTENCIA_CHANGED, { detail: { userId, remoteChanged } }));
};
let syncing = null;
const contextRequests = new Map();
const uuid = () => crypto.randomUUID();
async function storage() {
    const driver = getOfflineStorageDriver(); await driver.init();
    if (getEffectiveStorageEngine().engine === 'memory') throw new Error('No se pudo abrir el almacenamiento de asistencia. Reintentá.');
    return driver;
}
export async function contextoAsistencia({ refresh = true, force = false, withStatus = false, onCached } = {}) {
    const result = (value) => withStatus ? value : value.context;
    const session = await readStoredAuth(); if (!session?.user?.id) return null;
    const driver = await storage(); const id = `context:${session.user.id}`;
    const cache = await driver.get(store, id);
    if (cache?.payload && typeof onCached === 'function') await onCached(cache.payload);
    if (refresh && session.token && (force || !cache?.cachedAt || Date.now() - cache.cachedAt > 30000) && (typeof navigator === 'undefined' || navigator.onLine)) {
        const requestKey = `${session.user.id}:${session.token}`;
        if (contextRequests.has(requestKey)) return result(await contextRequests.get(requestKey));
        const request = Promise.resolve().then(async () => {
            try {
                const { data } = await requestWithFallback({ moduleName: 'jornadas', endpoint: '/react/asistencias/contexto', token: session.token, timeout: 10000 });
                if (!data?.ok) throw new Error('Contexto no confirmado.');
                await driver.put(store, { id, userId: Number(session.user.id), type: 'context', payload: data, cachedAt: Date.now() });
                return { context: data, error: null, fromCache: false };
            } catch (error) {
                // El caché permite informar sin conexión; cada consumidor puede distinguir una lectura fallida.
                return { context: cache?.payload || null, error, fromCache: true };
            }
        }).finally(() => { contextRequests.delete(requestKey); });
        contextRequests.set(requestKey, request);
        return result(await request);
    }
    return result({ context: cache?.payload || null, error: null, fromCache: true });
}
export async function leerAvisosLocales(userId) {
    const driver = await storage();
    return (await driver.getAll(store, null, { userId: Number(userId) })).filter((r) => r?.type === 'aviso' && r.userId === Number(userId)).sort((a,b) => b.payload.notificado_at.localeCompare(a.payload.notificado_at));
}
const toBase64 = (blob) => new Promise((resolve,reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1]); r.onerror = reject; r.readAsDataURL(blob); });
async function prepareFile(file, userId) {
    validarDocumentoAsistencia(file);
    const id = uuid(); const buffer = await file.arrayBuffer();
    const sha256 = [...new Uint8Array(await crypto.subtle.digest('SHA-256', buffer))].map((n) => n.toString(16).padStart(2,'0')).join('');
    const row = { id, type: 'file', userId: Number(userId), nombre: file.name, mime: file.type, sha256 };
    if (Capacitor.isNativePlatform()) {
        row.filePath = `rrhh-offline/${userId}/${id}`;
        await Filesystem.writeFile({ path: row.filePath, directory: Directory.Data, data: await toBase64(file), recursive: true });
    } else row.blob = file;
    return row;
}
export async function guardarAvisoLocal(payload, files = []) {
    const session = await readStoredAuth(); if (!session?.user?.id) throw new Error('Iniciá sesión para informar la ausencia.');
    if (files.length > ASISTENCIA_DOCUMENTOS_MAX) throw new Error('Podés adjuntar hasta 3 documentos.');
    const driver = await storage(); const attachments = [];
    for (const file of files) attachments.push(await prepareFile(file, session.user.id));
    const data = { ...payload, id: payload.id || uuid(), documentos: attachments.map((f) => ({ id: f.id, nombre: f.nombre, sha256: f.sha256 })) };
    const row = { id: data.id, userId: Number(session.user.id), type: 'aviso', payload: data, revision: uuid(), synced: false, syncError: null };
    await driver.atomicPutBatch([...attachments.map((value) => ({ storeName: OFFLINE_STORES.RRHH_FILES, value })), { storeName: store, value: row }]);
    notify(session.user.id); void sincronizarAvisos().catch(() => {}); return row;
}
async function readFile(row) {
    if (row.blob) return row.blob;
    const { data } = await Filesystem.readFile({ path: row.filePath, directory: Directory.Data });
    if (data instanceof Blob) return data;
    const binary = atob(data); const bytes = new Uint8Array(binary.length);
    for (let i=0;i<binary.length;i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: row.mime });
}

// Borrador privado, separado de la cola de avisos: escanear nunca registra una ausencia.
export const ASISTENCIA_SCAN_DRAFT_CHANGED = 'asistencia:scan-draft-changed';
const notifyDraft = () => { if (typeof window !== 'undefined') window.dispatchEvent(new Event(ASISTENCIA_SCAN_DRAFT_CHANGED)); };

async function limpiarArchivosBorrador(driver, row) {
    if (!row) return;
    for (const id of row.documentos || []) {
        const file = await driver.get(OFFLINE_STORES.RRHH_FILES, id);
        if (!file || file.userId !== row.userId) continue;
        if (file.filePath === `rrhh-offline/${row.userId}/${id}`) {
            try { await Filesystem.deleteFile({ directory: Directory.Data, path: file.filePath }); } catch { /* No impedir recuperar el borrador por un archivo ya ausente. */ }
        }
        await driver.delete(OFFLINE_STORES.RRHH_FILES, id);
    }
}

export async function guardarBorradorEscaneoAsistencia(payload, files, requestId = uuid(), expectedOwnerId = null, appliedResult = false) {
    const auth = await readStoredAuth();
    if (!auth?.user?.id) throw new Error('Iniciá sesión para preparar el escaneo.');
    if (files.length > ASISTENCIA_DOCUMENTOS_MAX) throw new Error('Podés adjuntar hasta 3 documentos.');
    const ownerId = Number(auth.user.id);
    if (expectedOwnerId != null && ownerId !== Number(expectedOwnerId)) throw new Error('La cuenta cambió. Reabrí el aviso desde tu cuenta.');
    const driver = await storage();
    const previousRow = await driver.get(store, `scan-draft:${ownerId}`);
    const previous = previousRow?.userId === ownerId && previousRow.type === 'scan_draft' ? previousRow : null;
    const attachments = [];
    for (const file of files) attachments.push(await prepareFile(file, ownerId));
    const active = await readStoredAuth();
    if (Number(active?.user?.id) !== ownerId) throw new Error('La cuenta cambió. Reabrí el aviso desde tu cuenta.');
    const row = { id: `scan-draft:${ownerId}`, type: 'scan_draft', userId: ownerId, requestId,
        payload: { ...payload, id: payload.id || previous?.payload?.id || uuid() },
        appliedScanResult: appliedResult ? requestId : previous?.requestId === requestId ? previous.appliedScanResult || null : null,
        documentos: attachments.map((file) => file.id), updatedAt: Date.now() };
    await driver.atomicPutBatch([...attachments.map((value) => ({ storeName: OFFLINE_STORES.RRHH_FILES, value })), { storeName: store, value: row }]);
    try { await limpiarArchivosBorrador(driver, previous); } catch { /* El nuevo lote ya está confirmado localmente. */ }
    notifyDraft();
    return row;
}

export async function leerEstadoBorradorEscaneoAsistencia(userId) {
    const driver = await storage();
    const row = await driver.get(store, `scan-draft:${userId}`);
    if (!row || row.type !== 'scan_draft' || row.userId !== Number(userId)) return null;
    return row;
}

export async function leerBorradorEscaneoAsistencia(userId) {
    const row = await leerEstadoBorradorEscaneoAsistencia(userId);
    if (!row) return null;
    const driver = await storage();
    const files = [];
    for (const id of row.documentos) {
        const document = await driver.get(OFFLINE_STORES.RRHH_FILES, id);
        if (!document || document.userId !== Number(userId)) throw new Error('No se pudo recuperar un adjunto del borrador.');
        files.push(new File([await readFile(document)], document.nombre, { type: document.mime }));
    }
    return { ...row, files };
}

export async function borrarBorradorEscaneoAsistencia(userId, requestId) {
    const driver = await storage();
    const row = await leerEstadoBorradorEscaneoAsistencia(userId);
    const deleted = await driver.compareAndDelete(store, `scan-draft:${userId}`, (actual) => actual?.userId === Number(userId) && actual.requestId === row?.requestId && (!requestId || actual.requestId === requestId));
    if (deleted) { try { await limpiarArchivosBorrador(driver, row); } catch {} notifyDraft(); }
    return deleted;
}
export function sincronizarAvisos() {
    if (syncing) return syncing;
    let changed = false;
    let remoteChanged = false;
    let ownerId = null;
    syncing = (async () => {
        const session = await readStoredAuth(); if (!session?.token || !session.user?.id) return { confirmed: 0, message: 'Iniciá sesión para enviar tus avisos.' };
        ownerId = Number(session.user.id);
        const driver = await storage(); const rows = (await leerAvisosLocales(session.user.id)).filter((r) => !r.synced).reverse();
        if (typeof navigator !== 'undefined' && !navigator.onLine) return { confirmed: 0, pending: rows.length, message: 'Estás sin conexión. El aviso y sus documentos se conservan en el dispositivo.' };
        let confirmed = 0; let errorMessage = null;
        for (const row of rows) {
            const current = await readStoredAuth(); if (!current?.token || Number(current.user.id) !== row.userId) break;
            try {
                const { data } = await requestWithFallback({ moduleName: 'jornadas', endpoint: '/react/asistencias/avisos', method: 'post', token: current.token, data: row.payload, timeout: 15000 });
                if (!data?.ok || data.confirmed !== row.id) throw new Error('Falta confirmar el aviso.');
                remoteChanged = true;
                for (const manifest of row.payload.documentos) {
                    const active = await readStoredAuth();
                    if (!active?.token || Number(active.user?.id) !== row.userId) throw new Error('La cuenta cambió. Reabrí tus avisos desde tu cuenta para continuar.');
                    const file = await driver.get(OFFLINE_STORES.RRHH_FILES, manifest.id);
                    if (!file || file.userId !== row.userId) throw new Error('No se encontró un documento del aviso en este dispositivo.');
                    const form = new FormData(); form.append('id', manifest.id); form.append('archivo', await readFile(file), file.nombre);
                    const uploaded = await requestWithFallback({ moduleName: 'jornadas', endpoint: `/react/asistencias/avisos/${row.id}/documentos`, method: 'post', token: active.token, data: form, timeout: 20000 });
                    if (!uploaded.data?.ok || uploaded.data.confirmed !== manifest.id) throw new Error('Falta confirmar un documento.');
                }
                const updated = await driver.mutate(store, row.id, (actual) => actual?.revision === row.revision && !actual.synced ? { ...actual, synced: true, syncError: null } : null);
                if (updated?.synced) { confirmed++; changed = true; }
            } catch (error) {
                errorMessage = error?.response || ['ERR_NETWORK','ECONNABORTED','ETIMEDOUT'].includes(error?.code)
                    ? jornadaSyncError(error).message.replaceAll('fichados','avisos').replaceAll('fichado','aviso')
                    : error?.message || 'No se pudo conectar. El aviso se conserva.';
                const updated = await driver.mutate(store, row.id, (actual) => actual?.revision === row.revision && actual.syncError !== errorMessage ? { ...actual, syncError: errorMessage } : null);
                if (updated) changed = true;
                if (!error?.response || error.response.status === 401) break;
            }
        }
        const pending = (await leerAvisosLocales(session.user.id)).filter((r) => !r.synced).length;
        return { confirmed, pending, message: errorMessage || (confirmed ? `Se enviaron ${confirmed} avisos.` : 'Avisos actualizados. No hay envíos pendientes.') };
    })().finally(() => { syncing = null; if (changed) notify(ownerId, remoteChanged); });
    return syncing;
}
